import React, { useMemo } from 'react';
import ReactMarkdown, { uriTransformer } from 'react-markdown';
import { useDispatch } from 'react-redux';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { tabsOpened } from 'store/actions/tabs';
import { InlineAudioPlayer, InlineImage, InlineProjectImage } from 'modules/AiAssistant/components/content/InlineMedia';

const AUDIO_RE = /\/data\/mp3\/r\d+\/d\d+\/\d+\.mp3$/;
const IMAGE_RE = /\/data\/png(-originals)?\/r\d+\/\d+\.png$/;
const PROJECT_IMAGE_RE = /\/ai-assistant\/projects\/\d+\/files\/.+\.(png|jpe?g|gif|svg|webp)$/i;
const CM_LINK_RE = /^cm:\/\/open\?/;

const transformUri = (uri: string) => (uri.startsWith('cm://') ? uri : uriTransformer(uri));

interface IProps {
  children: string;
  plugins?: any[];
}

const Markdown: React.FC<IProps> = React.memo(({ children, plugins }) => {
  const dispatch = useDispatch();

  const remarkPlugins = useMemo(() => [remarkGfm, ...(plugins || [])], [plugins]);

  const components = useMemo(() => {
    const handleCmLink = (href: string) => {
      const params = new URLSearchParams(href.replace('cm://open?', ''));
      const courseId = Number(params.get('courseId') || 0);
      const ref = Number(params.get('ref') || 0);
      const refIdParam = params.get('refId') || '';
      const navParam = params.get('nav');
      const navigationPath = navParam
        ? navParam.split(',').map((step) => {
            const [stepRef, stepRefId] = step.split(':').map(Number);
            return { ref: stepRef, refId: stepRefId };
          })
        : undefined;
      if (ref && refIdParam) {
        dispatch(tabsOpened({ refId: ref, courseId, search: refIdParam, navigationPath }));
      }
    };

    return {
      a: ({ node, href, children, ...props }: any) => {
        if (href && CM_LINK_RE.test(href)) {
          return (
            <span
              style={{ color: '#1967d2', cursor: 'pointer' }}
              onClick={() => handleCmLink(href)}
            >
              {children}
            </span>
          );
        }
        if (href && AUDIO_RE.test(href)) return <InlineAudioPlayer src={href} />;
        return <a href={href} {...props} target="_blank" rel="noopener noreferrer">{children}</a>;
      },
      img: ({ node, src, alt, ...props }: any) => {
        if (src && PROJECT_IMAGE_RE.test(src)) return <InlineProjectImage src={src} alt={alt} />;
        if (src && IMAGE_RE.test(src)) return <InlineImage src={src} alt={alt} />;
        return <img src={src} alt={alt} {...props} />;
      },
    };
  }, [dispatch]);

  return (
    <ReactMarkdown
      plugins={remarkPlugins}
      rehypePlugins={[rehypeRaw as any]}
      components={components}
      transformLinkUri={transformUri}
    >
      {children}
    </ReactMarkdown>
  );
});

export default Markdown;
